import { OrientedBoundingBox } from "@/components/r3f/utils/oriented-bounding-box";
import { CameraView, CameraViewType } from "@/utils/cam-view";
import { quantile } from "@/utils/statistics";
import { DEFAULT_PERSPECTIVE_FOV } from "@faro-lotv/lotv";
import {
  Box3,
  Euler,
  MathUtils,
  PerspectiveCamera,
  Quaternion,
  Vector3,
  Vector3Tuple,
} from "three";

/** A region box turned around the vertical axis, e.g. aligned to the walls of a building */
export type RegionBox = {
  /** The box in the frame turned by yaw */
  box: Box3;
  /** Angle around the vertical axis, in radians */
  yaw: number;
};

/** Share of the points treated as stray on each horizontal side and below the floor */
const STRAY_SHARE = 0.01;
/** Share of the points treated as stray above the roof, smaller as roofs are sampled sparsely */
const ROOF_STRAY_SHARE = 0.005;
/** Margin around the data, relative to its horizontal extent */
const HORIZONTAL_MARGIN = 0.05;
/** Margin below the floor and above the roof, in meters */
const VERTICAL_MARGIN = 0.5;
/** Height of a static scanner above the floor, in meters */
const SCANNER_HEIGHT = 1.5;
/** Fewer points do not describe the data */
const MIN_DATA_POINTS = 50;
/** Points closer than this many times their usual spacing are parts of the same object */
const LINK_SPACINGS = 4;
/** Shortest link between points, in meters */
const MIN_LINK_DISTANCE = 0.5;
/** Points used to estimate the spacing of the points */
const SPACING_SAMPLES = 500;
/** Parts of the data with fewer points than this share, or this count, are stray, unless they hold a scanner */
const MIN_PART_SHARE = 0.0005;
const MIN_PART_POINTS = 10;
/** Orientations tried for the box, one per degree over a quarter turn */
const YAW_STEPS = 90;
/** Points used to find the orientation */
const YAW_SAMPLES = 4000;
/** The box turns only if its footprint gets smaller by this share, so axis aligned data keeps an aligned box */
const MIN_YAW_GAIN = 0.05;
/** Points are kept in proportion to the density of their area, up to the density around this share of the points */
const DENSE_SHARE = 0.8;
/** The box of the dense data replaces the plain one only if it is this much smaller, so it is mostly sparse points */
const SPARSE_BOX_SHARE = 0.2;
/** A meter of height below the data with fewer points than this share of the fullest one is noise, e.g. reflections */
const DATA_LAYER_SHARE = 0.01;
/** Meters of noise that separate the data from what lies below, walls join the floors of a building */
const DATA_LAYER_GAP = 3;
/** Knuth's multiplicative hash, spreads the indices of the points evenly over [0, 2^32) */
const HASH_MULTIPLIER = 2654435761;
const HASH_RANGE = 2 ** 32;
/** Keys of the cells of the spatial index, column * CELL_KEY_STRIDE + row, unique within ±500k cells */
const CELL_KEY_STRIDE = 1_000_000;
const CELL_KEY_OFFSET = 500_000;
const UP = new Vector3(0, 1, 0);

/**
 * @param points The points
 * @param axis The coordinate to extract
 * @returns the coordinates of the points along the axis, sorted
 */
function sortedAxis(points: Vector3Tuple[], axis: number): number[] {
  return points.map((point) => point[axis]).sort((a, b) => a - b);
}

/**
 * @param sorted Coordinates sorted in ascending order
 * @returns the range of the coordinates without the stray ones, plus the margin
 */
function horizontalRange(sorted: number[]): [number, number] {
  const min = quantile(sorted, STRAY_SHARE);
  const max = quantile(sorted, 1 - STRAY_SHARE);
  const margin = (max - min) * HORIZONTAL_MARGIN;
  return [min - margin, max + margin];
}

/**
 * @param heights Heights of the points, sorted in ascending order
 * @returns the bottom of the data: going down from the fullest meter of height, the last meter holding a share of its
 * points before a gap of meters holding fewer, below it there is only noise, e.g. reflections
 */
function lowestDataLayer(heights: number[]): number {
  const bottom = Math.floor(heights[0]);
  const counts = new Array<number>(
    Math.floor(heights.at(-1) ?? bottom) - bottom + 1,
  ).fill(0);
  for (const height of heights) counts[Math.floor(height) - bottom]++;
  const peak = counts.reduce(
    (best, count, i) => (count > counts[best] ? i : best),
    0,
  );
  const minCount = counts[peak] * DATA_LAYER_SHARE;
  let lowest = peak;
  for (let i = peak - 1; i >= 0 && lowest - i <= DATA_LAYER_GAP; i--) {
    if (counts[i] >= minCount) lowest = i;
  }
  return bottom + lowest;
}

/**
 * @param points The points
 * @returns the median horizontal distance from a point to its closest neighbor, on a subset of the points
 */
function medianSpacing(points: Vector3Tuple[]): number {
  const byX = points
    .map((_, i) => i)
    .sort((a, b) => points[a][0] - points[b][0]);
  const step = Math.max(1, Math.floor(points.length / SPACING_SAMPLES));
  const spacings: number[] = [];
  for (let s = 0; s < byX.length; s += step) {
    const [x, , z] = points[byX[s]];
    let closest = Infinity;
    // The neighbors sorted by x stop being closer once the x distance alone is larger
    for (const direction of [-1, 1]) {
      for (let k = s + direction; k >= 0 && k < byX.length; k += direction) {
        const [ox, , oz] = points[byX[k]];
        if (Math.abs(ox - x) >= closest) break;
        closest = Math.min(closest, Math.hypot(ox - x, oz - z));
      }
    }
    spacings.push(closest);
  }
  return quantile(
    spacings.sort((a, b) => a - b),
    0.5,
  );
}

/**
 * Keeps the main parts of the data and the parts around the scanners, the stray points are further from them than the data is sparse.
 *
 * @param points The points of the data
 * @param scannerPositions World positions of the static scanners
 * @returns the points of the parts linked through close neighbors, seen from above, holding a scanner or many points
 */
function keepMainData(
  points: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
): Vector3Tuple[] {
  const link = Math.max(
    MIN_LINK_DISTANCE,
    LINK_SPACINGS * medianSpacing(points),
  );
  function cellOf(x: number, z: number): number {
    return (
      (Math.floor(x / link) + CELL_KEY_OFFSET) * CELL_KEY_STRIDE +
      Math.floor(z / link) +
      CELL_KEY_OFFSET
    );
  }
  const cells = new Map<number, number[]>();
  for (const [i, [x, , z]] of points.entries()) {
    const key = cellOf(x, z);
    const cell = cells.get(key);
    if (cell) cell.push(i);
    else cells.set(key, [i]);
  }
  function forEachNeighbor(
    [x, , z]: Vector3Tuple,
    visit: (j: number) => void,
  ): void {
    const center = cellOf(x, z);
    for (const dx of [-CELL_KEY_STRIDE, 0, CELL_KEY_STRIDE]) {
      for (const dz of [-1, 0, 1]) {
        for (const j of cells.get(center + dx + dz) ?? []) {
          if (Math.hypot(points[j][0] - x, points[j][2] - z) <= link) visit(j);
        }
      }
    }
  }

  const part = new Int32Array(points.length).fill(-1);
  const sizes: number[] = [];
  for (let start = 0; start < points.length; start++) {
    if (part[start] >= 0) continue;
    const id = sizes.length;
    part[start] = id;
    const queue = [start];
    for (let i = queue.pop(); i !== undefined; i = queue.pop()) {
      forEachNeighbor(points[i], (j) => {
        if (part[j] >= 0) return;
        part[j] = id;
        queue.push(j);
      });
    }
    sizes.push(0);
  }
  for (const id of part) sizes[id]++;
  const scanned = new Set<number>();
  for (const position of scannerPositions) {
    forEachNeighbor(position, (j) => scanned.add(part[j]));
  }
  const minPartSize = Math.max(MIN_PART_POINTS, points.length * MIN_PART_SHARE);
  const kept = points.filter(
    (_, i) => scanned.has(part[i]) || sizes[part[i]] >= minPartSize,
  );
  return kept.length >= MIN_DATA_POINTS ? kept : points;
}

/**
 * @param points The data points, without the stray ones
 * @param scannerPositions World positions of the static scanners of the data, if known
 * @returns the box around the points, without the stray points left and the margins
 */
function boxAround(
  points: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
): Box3 {
  const heights = sortedAxis(points, 1);
  const [minX, maxX] = horizontalRange(sortedAxis(points, 0));
  const [minZ, maxZ] = horizontalRange(sortedAxis(points, 2));
  const floor = scannerPositions.reduce(
    (lowest, position) => Math.min(lowest, position[1] - SCANNER_HEIGHT),
    Math.max(quantile(heights, STRAY_SHARE), lowestDataLayer(heights)),
  );
  const roof = quantile(heights, 1 - ROOF_STRAY_SHARE);
  return new Box3(
    new Vector3(minX, floor - VERTICAL_MARGIN, minZ),
    new Vector3(maxX, roof + VERTICAL_MARGIN, maxZ),
  );
}

/**
 * @param points The points
 * @param yaw Angle around the vertical axis, in radians
 * @returns the points in the frame turned by yaw
 */
function toYawFrame(points: Vector3Tuple[], yaw: number): Vector3Tuple[] {
  const point = new Vector3();
  return points.map((p) =>
    point.fromArray(p).applyAxisAngle(UP, -yaw).toArray(),
  );
}

/**
 * @param points The data points, without the stray ones
 * @returns the yaw of the smallest footprint without the stray points, aligned to the walls of buildings
 */
function findYaw(points: Vector3Tuple[]): number {
  const step = Math.max(1, Math.floor(points.length / YAW_SAMPLES));
  const samples = points.filter((_, i) => i % step === 0);
  function footprint(yaw: number): number {
    const turned = toYawFrame(samples, yaw);
    const [minX, maxX] = horizontalRange(sortedAxis(turned, 0));
    const [minZ, maxZ] = horizontalRange(sortedAxis(turned, 2));
    return (maxX - minX) * (maxZ - minZ);
  }
  let best = { yaw: 0, area: footprint(0) * (1 - MIN_YAW_GAIN) };
  for (let i = 1; i < YAW_STEPS; i++) {
    const yaw = MathUtils.degToRad((i * 90) / YAW_STEPS);
    const area = footprint(yaw);
    if (area < best.area) best = { yaw, area };
  }
  return best.yaw;
}

/**
 * @param points The points
 * @param densities Density of the data around each point
 * @returns the points kept with a probability proportional to their density, all of them in the densest areas
 */
function thinSparse(
  points: Vector3Tuple[],
  densities: number[],
): Vector3Tuple[] {
  const reference = quantile(
    [...densities].sort((a, b) => a - b),
    DENSE_SHARE,
  );
  // A fixed pseudo random sequence, so the same data always gets the same box
  return points.filter(
    (_, i) =>
      ((i * HASH_MULTIPLIER) % HASH_RANGE) / HASH_RANGE <
      densities[i] / reference,
  );
}

/**
 * @param points The points of the data
 * @param scannerPositions World positions of the static scanners
 * @returns the region of the points without the stray ones, undefined if there are too few points
 */
function regionOf(
  points: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
): RegionBox | undefined {
  if (points.length < MIN_DATA_POINTS) return;
  const kept = keepMainData(points, scannerPositions);
  const yaw = findYaw(kept);
  return { box: boxAround(toYawFrame(kept, yaw), scannerPositions), yaw };
}

/**
 * @param region A region box
 * @returns the area of its footprint
 */
function footprintArea({ box }: RegionBox): number {
  return (box.max.x - box.min.x) * (box.max.z - box.min.z);
}

/**
 * Computes the region of the data without the stray points (sky, reflections, noise),
 * turned to the smallest footprint, so the box follows the walls of a building and cuts the trees around it.
 * Floors under a roof can be missing from a coarse sample, so the floor is also derived from the scanners height.
 * A coarse sample spreads evenly over space, so a wide sparse halo (e.g. the noise of mobile mapping) can outweigh the data:
 * with the densities, the box of the dense data is used when the plain box is mostly sparse points.
 *
 * @param dataPoints World positions of points of the data, e.g. a coarse sample of the point cloud
 * @param scannerPositions World positions of the static scanners of the data, if known
 * @param densities Density of the data around each point, e.g. the points of the finer LOD node containing it
 * @returns the region box, or undefined if there are too few points
 */
export function computeDataRegion(
  dataPoints: Vector3Tuple[],
  scannerPositions: Vector3Tuple[],
  densities?: number[],
): RegionBox | undefined {
  const region = regionOf(dataPoints, scannerPositions);
  if (!region || !densities) return region;
  const dense = regionOf(thinSparse(dataPoints, densities), scannerPositions);
  return dense &&
    footprintArea(dense) < SPARSE_BOX_SHARE * footprintArea(region)
    ? dense
    : region;
}

/**
 * @param region A region box
 * @returns the same region as a clipping box
 */
export function regionToObb({ box, yaw }: RegionBox): OrientedBoundingBox {
  const rotation = new Quaternion().setFromAxisAngle(UP, yaw);
  return {
    position: box.getCenter(new Vector3()).applyQuaternion(rotation).toArray(),
    size: box.getSize(new Vector3()).toArray(),
    rotation: rotation.toArray(),
  };
}

/**
 * @param obb A clipping box turned only around the vertical axis
 * @returns the same box as a region box
 */
export function obbToRegion({
  position,
  size,
  rotation,
}: OrientedBoundingBox): RegionBox {
  const yaw = new Euler().setFromQuaternion(
    new Quaternion().fromArray(rotation),
    "YXZ",
  ).y;
  const center = new Vector3().fromArray(position).applyAxisAngle(UP, -yaw);
  return {
    box: new Box3().setFromCenterAndSize(center, new Vector3().fromArray(size)),
    yaw,
  };
}

/** A screen area in normalized device coordinates */
type ScreenArea = {
  left: number;
  right: number;
  bottom: number;
  top: number;
};

/** The canvas area free from the right toolbar and the hint banner of the viewer */
const DEFAULT_FRAMING_AREA: ScreenArea = {
  left: -0.9,
  right: 0.9,
  bottom: -0.9,
  top: 0.75,
};
const FIT_ITERATIONS = 24;

/**
 * @param region The region box
 * @returns the world positions of the corners of the box
 */
function boxCorners({ box, yaw }: RegionBox): Vector3[] {
  const { min, max } = box;
  return Array.from({ length: 8 }, (_, i) =>
    new Vector3(
      i & 1 ? max.x : min.x,
      i & 2 ? max.y : min.y,
      i & 4 ? max.z : min.z,
    ).applyAxisAngle(UP, yaw),
  );
}

/**
 * Moves the camera along its view direction until the whole region box is in a screen area
 *
 * @param region The region box
 * @param rotation The camera orientation to keep
 * @param aspect Width / height of the canvas
 * @param area Where the box must be, usually the canvas area free from toolbars
 * @returns the camera view orbiting around the center of the box
 */
export function computeRegionFramingView(
  region: RegionBox,
  rotation: Quaternion,
  aspect: number,
  area: ScreenArea = DEFAULT_FRAMING_AREA,
): CameraView {
  const { box, yaw } = region;
  const target = box.getCenter(new Vector3()).applyAxisAngle(UP, yaw);
  const corners = boxCorners(region);
  const camera = new PerspectiveCamera(DEFAULT_PERSPECTIVE_FOV, aspect);
  camera.quaternion.copy(rotation);
  const direction = camera.getWorldDirection(new Vector3());

  function fits(distance: number): boolean {
    camera.position.copy(target).addScaledVector(direction, -distance);
    camera.updateMatrixWorld();
    return corners.every((corner) => {
      const isInFront =
        corner.clone().applyMatrix4(camera.matrixWorldInverse).z < 0;
      const { x, y } = corner.clone().project(camera);
      return (
        isInFront &&
        x >= area.left &&
        x <= area.right &&
        y >= area.bottom &&
        y <= area.top
      );
    });
  }

  let low = 0;
  let high = Math.max(box.max.distanceTo(box.min), 1);
  for (let i = 0; i < FIT_ITERATIONS && !fits(high); i++) high *= 2;
  for (let i = 0; i < FIT_ITERATIONS; i++) {
    const mid = (low + high) / 2;
    if (fits(mid)) high = mid;
    else low = mid;
  }

  return {
    position: target.clone().addScaledVector(direction, -high),
    rotation: rotation.clone(),
    zoomLevel: DEFAULT_PERSPECTIVE_FOV,
    type: CameraViewType.perspective,
    target,
  };
}
